import { Service, inject } from '@angular/core';
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';

import { catchError, combineLatest, distinctUntilChanged, filter, map, shareReplay, throwError } from 'rxjs';

import { v4 as uuid } from 'uuid';
import { HttpLink } from 'apollo-angular/http';
import { SetContextLink } from '@apollo/client/link/context';
import { ApolloClient, ApolloLink, InMemoryCache, CombinedGraphQLErrors, ServerError } from '@apollo/client/core';
import { TmtLoggerService } from 'tmt-logger';
import { ErrorLink } from '@apollo/client/link/error';
import { createAuthLink, AUTH_TYPE } from 'aws-appsync-auth-link';
import { createSubscriptionHandshakeLink } from 'aws-appsync-subscription-link';

import { environment } from 'src/environments/environment';
import { AuthService } from 'src/app/services/auth.service';
import { LaunchDarklyService } from 'src/app/services/launchdarkly.service';
import { PreferencesService } from 'src/app/services/preferences.service';
import { LaunchDarklyFlags } from 'src/environments/environment.interfaces';
import { CorrelationIdError } from 'src/app/models/correlation-id-error';

/**
 * Service providing apollo clients.
 */
@Service()
export class ApolloClientService {
	/** To create httpLink for client. */
	private httpLink = inject(HttpLink);

	private authService = inject(AuthService);

	/** To get feature flags for current user. */
	private launchdarklyService = inject(LaunchDarklyService);

	/** To log result and errors. */
	private loggerService = inject(TmtLoggerService);

	/** To get staging preference. */
	private preferencesService = inject(PreferencesService);

	/**
	 * Observable of data which is common between all available clients.
	 */
	private commonClientConfiguration$ = this.authService.userData$.pipe(
		map(userData => {
			const sessionId = userData.sessionID || 'anonymous';
			const headerLink = new SetContextLink(async prevContext => {
				this.loggerService.logDebug('setContext');
				const correlationId = uuid();
				const requestId = uuid();
				this.loggerService.logDebug('graphql sessionId: ' + sessionId);

				return {
					headers: {
						...prevContext.headers,
						'x-correlation-id': correlationId,
						'x-request-id': requestId,
					},
				};
			});

			const correlationIdErrorLink = new ApolloLink((operation, forward) => {
				return forward(operation).pipe(
					catchError((error: any) => {
						const headers = operation.getContext().headers ?? {};
						const correlationId = headers instanceof HttpHeaders ? headers.get('x-correlation-id') : headers['x-correlation-id'];
						return throwError(() => new CorrelationIdError(error, correlationId ?? undefined));
					}),
				);
			});

			// Configure error handling.
			const errorLink = new ErrorLink(({ error, operation }) => {
				this.loggerService.logDebug('onError');

				const headers = operation.getContext().headers ?? {};
				const correlationId = headers instanceof HttpHeaders ? headers.get('x-correlation-id') : headers['x-correlation-id'];

				// GraphQL execution errors (i.e. the server returned an `errors` array)
				if (CombinedGraphQLErrors.is(error)) {
					error.errors.forEach(({ message, locations, path }) => {
						this.loggerService.logError(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`, correlationId ?? undefined);
					});
				}

				// Network / transport errors (HTTP 4xx/5xx, parse errors, fetch errors, etc.)
				if (
					((error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) ||
						(error instanceof ServerError && (error.statusCode === 401 || error.statusCode === 403))) &&
					userData.isLoggedIn
				) {
					this.authService.clearSession();
				}
				// NOTE: JSON.stringify(Error) is often "{}", so include message/status explicitly.
				this.loggerService.logError(`[Network error]: ${String(error)}${ServerError.is(error) ? ` (status ${error.statusCode})` : ''}`, correlationId ?? undefined);
			});

			const timeStartLink = new ApolloLink((operation, forward) => {
				this.loggerService.logDebug('forward start');
				const headers = operation.getContext().headers ?? {};
				const correlationId = headers instanceof HttpHeaders ? headers.get('x-correlation-id') : headers['x-correlation-id'];
				const startTime = new Date();
				this.loggerService.logInformation(`[${operation.operationName}] Sending request`, correlationId ?? undefined);
				operation.setContext({ start: startTime });
				this.loggerService.logDebug('forward end');
				return forward(operation);
			});

			const timeEndLink = new ApolloLink((operation, forward) => {
				this.loggerService.logDebug('ApolloLink');
				return forward(operation).pipe(
					map(data => {
						const headers = operation.getContext().headers ?? {};
						const correlationId = headers instanceof HttpHeaders ? headers.get('x-correlation-id') : headers['x-correlation-id'];
						const duration = new Date().getTime() - operation.getContext()['start'].getTime();
						this.loggerService.logInformation(`[${operation.operationName}] took ${duration} to complete`, correlationId ?? undefined);
						return data;
					}),
				);
			});

			return {
				headerLink,
				errorLink,
				timeStartLink,
				timeEndLink,
				correlationIdErrorLink,
				sessionId, // NEW: expose sessionId so we can use it in authLink
			};
		}),
		shareReplay(1),
	);

	/**
	 * Staging setting only available if feature flag is enabled
	 */
	public stagingAvailable$ = this.launchdarklyService.launchDarklyFlagsWithFallback$.pipe(
		map(flags => flags[LaunchDarklyFlags.Staging]),
		shareReplay(1),
	);

	/**
	 * Observable of current user environment preferences.
	 * Provides true if staging is available and selected by user.
	 */
	public isStaging$ = combineLatest({
		prefersStaging: this.preferencesService.preferences$.pipe(map(preferences => preferences.staging)),
		stagingAvailable: this.stagingAvailable$,
	}).pipe(
		map(source => source.prefersStaging && source.stagingAvailable),
		distinctUntilChanged(),
	);

	/**
	 * Observable of apollo client for communicating with abstraction layer (AppSync).
	 */
	public abstractionLayerClient$ = combineLatest({
		commonConfiguration: this.commonClientConfiguration$,
		isStaging: this.isStaging$,
	}).pipe(
		map(source => {
			// Set uri based on environment from LaunchDarkly flags.
			const url = source.isStaging ? environment.abstractionLayerStagingUrl : environment.abstractionLayerReleaseUrl;
			const region = environment.appSyncRegion;
			const auth = {
				type: AUTH_TYPE.AWS_LAMBDA as const,
				token: source.commonConfiguration.sessionId,
			};

			// Base HTTP link (used by the subscription handshake link for queries/mutations)
			const httpLink = this.httpLink.create({ uri: url });

			// AppSync auth + subscription links
			const appSyncAuthLink = createAuthLink({ url, region, auth });
			const appSyncSubscriptionLink = createSubscriptionHandshakeLink({ url, region, auth }, httpLink);

			const link = ApolloLink.from([
				source.commonConfiguration.correlationIdErrorLink,
				source.commonConfiguration.errorLink,
				source.commonConfiguration.headerLink,
				source.commonConfiguration.timeStartLink,
				source.commonConfiguration.timeEndLink,
				appSyncAuthLink,
				appSyncSubscriptionLink,
			]);

			// Return client to subscribers.
			return new ApolloClient({
				link,
				cache: new InMemoryCache({
					typePolicies: {},
				}),
			});
		}),
		filter(client => !!client),
		shareReplay(1),
	);

	/**
	 * Observable of apollo client for communicating with abstraction layer.
	 */
	public authLayerClient = combineLatest({
		commonConfiguration: this.commonClientConfiguration$,
		launchDarklyFlags: this.launchdarklyService.launchDarklyFlagsWithFallback$,
	}).pipe(
		map(source => {
			// Set uri based on environment from LaunchDarkly flags.
			const url = source.launchDarklyFlags[LaunchDarklyFlags.Staging] ? environment.authLayerStagingUrl : environment.authLayerReleaseUrl;
			const region = environment.appSyncRegion;
			const auth = {
				type: AUTH_TYPE.AWS_LAMBDA as const,
				token: source.commonConfiguration.sessionId,
			};

			// Base HTTP link (used by the subscription handshake link for queries/mutations)
			const httpLink = this.httpLink.create({ uri: url });

			// AppSync auth + subscription links
			const appSyncAuthLink = createAuthLink({ url, region, auth });
			const appSyncSubscriptionLink = createSubscriptionHandshakeLink({ url, region, auth }, httpLink);

			const link = ApolloLink.from([
				source.commonConfiguration.correlationIdErrorLink,
				source.commonConfiguration.errorLink,
				source.commonConfiguration.headerLink,
				source.commonConfiguration.timeStartLink,
				source.commonConfiguration.timeEndLink,
				appSyncAuthLink,
				appSyncSubscriptionLink,
			]);

			// Return client to subscribers.
			return new ApolloClient({
				link,
				cache: new InMemoryCache({
					typePolicies: {},
				}),
			});
		}),
		filter(client => !!client),
		shareReplay(1),
	);
}
